import { AfterViewInit, Component, Inject, Injector, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { map, Observable, tap, of, forkJoin } from 'rxjs';
import { AgentDialogComponent } from 'src/app/modals/agent-dialog/agent-dialog.component';
import { Agent } from 'src/app/models/agent.model';
import { AuthService } from 'src/app/services';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { AgentService } from 'src/app/services/agent.service';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { segsRouteConfig } from 'src/app/shared/constants/segsRouteConfig.constants';
import { ARCHIVED_RECORDS_EXPORT_DISABLED_REASON } from 'src/app/shared/constants/archivedRecords.constants';
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { OverviewConfig } from 'src/app/shared/grid/core-grid/overviewConfig.model';
import { AgentDetailsPopupComponent } from 'src/app/common-components/agent-details-popup/agent-details-popup.component';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import { ChildColumnConfig, ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { Lookup } from 'src/app/models/lookup.model';
import { AgentOrgLevelService } from 'src/app/services/agent-org-level.service';
import { AgentToGroupDialogComponent, IAgentToGroupDialog } from 'src/app/modals/agent-to-group-dialog/agent-to-group-dialog.component';
import { AgentSiteDialogComponent, IAgentSiteDialogData } from 'src/app/modals/agent-site-dialog/agent-site-dialog.component';
import { GridViewService } from 'src/app/services/grid-view.service';
import { TranslateService } from '@ngx-translate/core';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { HazardCategory, HAZARD_CATEGORY_DISPLAY_LABELS } from 'src/app/enums/hazardCategory.enum';
import { AdjustmentClassificationDescriptions, AdjustmentClassificationEnum } from 'src/app/enums/adjustmentClassification.enum';
import { MasterAgentService } from 'src/app/services/master-agent.service';
import { ParameterService } from 'src/app/services/parameter.service';
import { ISearchFieldData, WIDGET_CONFIG } from 'src/app/shared/grid/baseGrid.component';
import { WidgetConfig } from 'src/app/models/dashboard';
import { GridPermissions, gridToPermissionFlags } from 'src/app/shared/grid/core-grid/gridPermissions.enum';

@Component({
  standalone: false,
  selector: 'app-agents-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss', './agents-list.component.scss'],
})
export class AgentsListComponent extends CoreGridComponent<Agent> implements OnInit, AfterViewInit {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  protected override get enableCheckboxSelection(): boolean { return true; }

  totalAgents: number = 0;
  agentsMissingJurisdiction: number = 0;
  organizationLevels: Lookup[] = [];
  masterAgents: Lookup[] = [];
  parameters: Lookup[] = [];
  allParameters: Lookup[] = [];
  private overviewLoaded = false;
  private showArchivedOnly = false;

  constructor(
    protected agentService: AgentService,
    toastService: ToastService,
    commonService: CommonService,
    dialog: MatDialog,
    authService: AuthService,
    private agentJurisdictionService: AgentJurisdictionService,
    private organizationLevelService: OrganizationLevelService,
    private agentOrgLevelService: AgentOrgLevelService,
    gridViewService: GridViewService,
    private translateService: TranslateService,
    gridSettingsService: GridSettingsService,
    private masterAgentService: MasterAgentService,
    private parameterService: ParameterService,
    private injector: Injector,
    private route: ActivatedRoute,
    @Optional() @Inject(MAT_DIALOG_DATA) public searchFieldData: ISearchFieldData,
    @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig
  ) {
    super(toastService,
      dialog,
      commonService,
      authService,
      gridViewService,
      gridSettingsService,
      widgetConfig
    );
    this.gridService = agentService;
  }

  protected get objectId(): string {
    return RolePermissionObjects.Agent;
  }

  // Add To My Site and Remove write GLOBAL_Agent_OrgLevel, and PermissionCheckInterceptor
  // authorizes each independently against that object (not GLOBAL_Agent), so gate them on
  // the join entity's own Create/Delete flag rather than the parent grid's canCreate/canDelete.
  protected get canAddToMySite(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.AgentOrgLevel, gridToPermissionFlags[GridPermissions.Create]);
  }

  protected get canRemoveOrgLevel(): boolean {
    return this.permissionService.hasPermissionFlag(RolePermissionObjects.AgentOrgLevel, gridToPermissionFlags[GridPermissions.Delete]);
  }

  get hasOrgFilter() {
    return true;
  }

  protected override get isGridViewEnabled(): boolean { return true; }

  override initializeProperties(): void {
    this.addEntityTitle = 'Agent';
    this.searchPlaceholder = 'Search Agents';
    this.deleteActionLabel = 'Archive';
    this.deleteActionIcon = 'archive';
  }

  override initializeColumns(): void {
    this.columnConfigs = [
      {
        name: nameOf<Agent>(x => x.name), displayName: 'Name',
        cssClass: () => 'left-aligned cursor-pointer',
        headerCssClass: () => 'left-aligned',
        onClick: (row: Agent) => {
          if (!this.showArchivedOnly) {
            this.showAgentDetails(row);
          }
        }
      },
      { name: nameOf<Agent>(x => x.casId), displayName: 'CAS Number' },
      { name: nameOf<Agent>(x => x.agentForm), displayName: 'Agent Form' },
      { name: nameOf<Agent>(x => x.agentType), displayName: 'Agent Type', cellTemplate: (row: Agent) => this.translateService.instant(SampleTypeEnum[row.agentType] ?? '-'), enumFilterMap: this.enumToValueNameArray(SampleTypeEnum) },
      { name: nameOf<Agent>(x => x.hazardCategory), displayName: 'Hazard Category', cellTemplate: (row: Agent) => row.hazardCategory != null ? this.translateService.instant(HAZARD_CATEGORY_DISPLAY_LABELS[row.hazardCategory]) : '-', enumFilterMap: this.enumToValueNameArray(HazardCategory) },
      { name: nameOf<Agent>(x => x.healthAdvisory), displayName: 'Health Advisory' },
      { name: nameOf<Agent>(x => x.specificRecommendations), displayName: 'Specific Recommendations' },
      { name: nameOf<Agent>(x => x.adjustmentClassification), displayName: 'Adjustment Classification', cellTemplate: (row: Agent) => AdjustmentClassificationDescriptions[row.adjustmentClassification], enumFilterMap: this.enumToValueNameArray(AdjustmentClassificationEnum) },
      { name: nameOf<Agent>(x => x.masterAgentId), displayName: 'Master Agent', cellTemplate: (row: Agent) => this.masterAgents?.find(m => m.id === row.masterAgentId)?.name || '-' },
      { name: nameOf<Agent>(x => x.analyteId), displayName: 'Analyte', cellTemplate: (row: Agent) => this.allParameters?.find(p => p.id === row.analyteId)?.name || '-' },
      { name: nameOf<Agent>(x => x.consequenceRating), displayName: 'Consequence Rating' },
      {
        name: nameOf<Agent>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (row: Agent) => this.organizationLevels?.find(org => org.id === row.organizationLevelId)?.name || 'N/A'
      },
      {
        name: nameOf<Agent>(x => x.addedToMySite), displayName: '', isDisplayNameConfigurable: false,
        cssClass: () => this.showArchivedOnly ? 'd-none' : '',
        headerCssClass: () => this.showArchivedOnly ? 'd-none' : '',
        cellTemplate: (row: Agent) => '',
        children: (row: Agent) => {
          if (!row?.addedToMySite) {
            if (!this.canAddToMySite) {
              return [];
            }
            return [
              new ChildColumnConfig<Agent>({
                onClick: agent => this.addToSite(agent.id),
                cssClass: () => 'agent-btn main-btn',
                cellTemplate: () => this.translateService.instant('Add To My Site'),
              }),
            ]
          }
          // The association is hierarchy-wide (AddedToMySiteGridService) but Remove deletes at
          // exactly the selected org level. When it's held at a descendant only, Remove would
          // silently no-op, so route to the Agent's details view instead of offering Remove.
          if (!row.addedAtSelectedOrgLevel) {
            return [
              new ChildColumnConfig<Agent>({
                onClick: agent => this.showAgentDetails(agent),
                cssClass: () => 'main-btn agent-added-btn agent-added-btn-linked',
                cellTemplate: () => this.translateService.instant('Added'),
              }),
            ]
          }

          const children: ChildColumnConfig<Agent>[] = [
            new ChildColumnConfig<Agent>({
              cssClass: () => 'main-btn agent-added-btn',
              cellTemplate: () => this.translateService.instant('Added'),
              readonly: () => true
            }),
          ];
          if (this.canRemoveOrgLevel) {
            children.push(
              new ChildColumnConfig<Agent>({
                onClick: agent => this.openRemoveFromSiteDialog(agent.id),
                cssClass: () => 'agent-btn agent-remove-btn main-btn ml-2',
                cellTemplate: () => this.translateService.instant('Remove')
              }),
            );
          }
          return children;
        }
      },
      { name: 'action', displayName: 'Actions' }
    ];
  }

  override objectFilterMap(propertyName: string): any | undefined {
    switch (propertyName) {
      case nameOf<Agent>(x => x.masterAgentId): return this.masterAgentService;
      case nameOf<Agent>(x => x.analyteId): return this.parameterService;
      case nameOf<Agent>(x => x.organizationLevelId): return this.organizationLevelService;
      default: return undefined;
    }
  }

  protected override onSelectionChanged(): void {
    this.setAdditionalButton();
  }

  setAdditionalButton() {
    const agentIds = this.selectionService!.selectedItems.map(res => res.id);
    const agentToGroupModel: IAgentToGroupDialog = {
      id: '', agentIds: agentIds
    };
    this.additionalButtons = [
      { title: 'Add/Create Agent Group', readonly: agentIds.length === 0, onClick: () => this.openDialog(AgentToGroupDialogComponent, agentToGroupModel) },
      { title: this.showArchivedOnly ? 'Show Active Agents' : 'Show Archived Agents', readonly: false, onClick: () => this.toggleArchivedView() },
    ];
  }

  override openEntityDeleteDialog(agent: Agent): void {
    if (this.showArchivedOnly) {
      this.isLoading = true;
      this.agentService.restore(agent.id).subscribe({
        next: () => {
          this.toastService.showSuccess('Agent restored successfully', true);
          this.loadPage();
        },
        error: this.handleError
      });

      return;
    }

    this.openDeleteDialog(DeleteDialog, agent, this.agentService, 'Archive Agent', undefined, undefined, () => this.removeRow(agent.id));
  }

  override loadPage(): void {
    if (!this.showArchivedOnly) {
      super.loadPage();
      return;
    }

    this.isLoading = true;
    this.agentService.getDeletedList(
      this.searchField?.nativeElement?.value ?? '',
      this.sortActive || this.getDefaultOrderByColumn(),
      this.defaultOrderBy || 'asc',
      this.paginator?.pageIndex ?? 0,
      this.paginator?.pageSize ?? this.getPageSize(),
    ).subscribe({
      next: (result) => {
        this.dataSource.updateData(result.data);
        this.paginator.length = result.count;
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private toggleArchivedView(): void {
    this.showArchivedOnly = !this.showArchivedOnly;
    this.editDisabled = this.showArchivedOnly;
    this.deleteDisabled = this.showArchivedOnly;
    this.exportDisabledReason = this.showArchivedOnly ? ARCHIVED_RECORDS_EXPORT_DISABLED_REASON : null;
    this.headerClassByColumn[nameOf<Agent>(x => x.addedToMySite)] = this.showArchivedOnly ? 'd-none' : '';    
    this.additionalActions = this.showArchivedOnly ? [
      {
        name: 'Restore',
        icon: 'fas fa-undo',
        action: (row: Agent) => this.openEntityDeleteDialog(row)
      }
    ] : [];
    
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.clearRowSelection();
    this.setAdditionalButton();
    this.loadPage();
  }

  showAgentDetails(row: Agent): void {
    this.openFlyoutInternal(AgentDetailsPopupComponent, row.id, '80vw', true);
  }

  addToSite(id: string) {
    const data: IAgentSiteDialogData = {
      id: '',
      agentId: id,
      organizationLevelId: this.selectedOrg?.id
    };
    const dialogRef = this.dialog.open(AgentSiteDialogComponent, {
      data: data,
      width: '600px',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.loadComponentData();
          this.toastService.showSuccess('Agent assigned to Organization Level successfully', true);
        }
      }
    });
  }

  openRemoveFromSiteDialog(id: string) {
    const data: IDeleteDialogData = {
      id: id,
      formDeletes: true,
      customDelete: () => this.agentOrgLevelService.delete(id, this.selectedOrg.id),
      title: this.translateService.instant('Agent from Site'),
      service: this.agentOrgLevelService,
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: data,
      width: '440px',
      disableClose: true
    });
    this.handleDialogClose(dialogRef);
  }

  private handleError = (error: unknown) => {
    this.toastService.showError(error);
    this.isLoading = false;
  };

  ngOnInit(): void {
    this.searchDialogRef = this.injector.get(MatDialogRef<AgentsListComponent>, null);
    super.ngOnInit();
    forkJoin({
      organizationLevels: this.organizationLevelService.getAll(),
      masterAgents: this.masterAgentService.getAll(),
      parameters: this.parameterService.getAllExcludingNotificationOnly(),
      allParameters: this.parameterService.getAll()
    }).subscribe({
      next: ({ organizationLevels, masterAgents, parameters, allParameters }) => {
        this.organizationLevels = organizationLevels;
        this.masterAgents = masterAgents;
        this.parameters = parameters;
        this.allParameters = allParameters;
      }
    });
    this.setAdditionalButton();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
    this.applySearchQueryParam();
  }
  private applySearchQueryParam(): void {
    const search = this.route.snapshot.queryParams.search?.trim();
    if (!search || !this.searchField?.nativeElement) {
      return;
    }

    this.searchField.nativeElement.value = search;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadPage();
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [
      new OverviewConfig({
        title: this.translateService.instant('TOTAL AGENTS'),
        value: this.totalAgents,
        onClick: () => this.showAllAgents()
      }),
      new OverviewConfig({
        title: this.translateService.instant('AGENTS MISSING JURISDICTION'),
        value: this.agentsMissingJurisdiction,
        onClick: () => this.showAllAgents()
      }),
    ]
  }

  showAllAgents() { /* yet to be implemented */ }

  override initializeTabs() {
    return segsRouteConfig;
  }

  override getOverviewData(): Observable<void> {
    if (!this.selectedOrg?.id) {
      return of(undefined);
    }
    return this.agentJurisdictionService.getAgentsOverviewData(this.selectedOrg.id)
      .pipe(
        tap(res => {
          this.totalAgents = res.agentsCount;
          this.agentsMissingJurisdiction = res.agentsMissingJurisdictionCount;
          this.overviewLoaded = true;
        }),
        map(() => undefined)
      );
  }

  override loadComponentData(): void {
    if (!this.overviewLoaded && this.selectedOrg?.id) {
      this.loadOverviewData();
    }
    super.loadComponentData();
  }

  override openEntityDialog(agentId?: string): void {
    const agentModel: IFormDialogData = {
      id: agentId
    };
    this.openDialog(AgentDialogComponent, agentModel, false, '760px');
  }
}
